import { cantidadDe, formatoCantidad, formatoPrecio, haceCuanto } from '@/lib/utils/formato-precio';
import { camposEnPalabras } from './reglas-en-palabras';
import type {
  CambioSinAplicar,
  CandidatoDelRecalculo,
  EstadoDelRecalculo,
  PorQueCambia,
  ReglasSinRecalcular,
  ResumenDelRecalculo,
} from '../types/pricing-rules.types';

/**
 * Las palabras de «Aplicar las reglas y recalcular» (lote C1, spec 246 punto 8;
 * maqueta #c1-recalcular). Puras: la vista previa, Por decidir y Hoy las dicen
 * igual.
 */

/** Por qué le cambia el precio, en una frase corta sin punto final. */
export function porQueEnTexto(p: PorQueCambia): string {
  switch (p.tipo) {
    case 'SIN_ESTRATEGIA':
      return p.lado === 'PISO'
        ? `«${p.regla}» pide al menos ${formatoPrecio(p.limite)}`
        : `«${p.regla}» deja hasta ${formatoPrecio(p.limite)}`;
    case 'YA_ESPERABA':
      return 'ya esperaba: se actualiza';
    case 'NUNCA_REVISADO':
      return 'nunca se había revisado';
    case 'NO_SE_REVISABA':
      return 'su competencia no había cambiado: no se volvía a revisar';
    case 'CON_LAS_REGLAS_DE_HOY':
      return 'con las reglas de hoy';
    case 'CONFLICTO':
      return p.piso && p.techo
        ? `«${p.piso.regla}» pide desde ${formatoPrecio(p.piso.precio)} y «${p.techo.regla}» deja hasta ${formatoPrecio(p.techo.precio)}`
        : 'las reglas no se pueden cumplir todas';
  }
}

/** Quién lo propone: la estrategia, «Sin estrategia» o «Conflicto entre reglas». */
export function quienLoPropone(c: Pick<CandidatoDelRecalculo, 'estrategia' | 'conflicto'>): string {
  if (c.conflicto) return 'Conflicto entre reglas';
  return c.estrategia?.name ?? 'Sin estrategia';
}

export type FiltroDelRecalculo = 'TODOS' | 'CON_ESTRATEGIA' | 'SIN_ESTRATEGIA' | 'CONFLICTO';

/** Los candidatos de un filtro de la tabla. Los conflictos sólo van en el suyo. */
export function filtrarCandidatos(
  candidatos: CandidatoDelRecalculo[],
  filtro: FiltroDelRecalculo
): CandidatoDelRecalculo[] {
  switch (filtro) {
    case 'TODOS':
      return candidatos;
    case 'CON_ESTRATEGIA':
      return candidatos.filter((c) => !c.conflicto && c.estrategia != null);
    case 'SIN_ESTRATEGIA':
      return candidatos.filter((c) => !c.conflicto && c.estrategia == null);
    case 'CONFLICTO':
      return candidatos.filter((c) => c.conflicto);
  }
}

/** «De los 211, 66 tienen estrategia… 4.193 quedan igual. Los 1.702 sin costo…». */
export function repartoEnPalabras(r: ResumenDelRecalculo): string {
  const partes: string[] = [];
  if (r.cambian > 0) {
    if (r.cambian === 1) {
      partes.push(
        r.conEstrategia === 1
          ? 'Ese tiene estrategia (se recalcula con su método).'
          : 'Ese no tiene estrategia (no cumple una regla: se le propone el precio más cercano que la cumple).'
      );
    } else {
      partes.push(
        `De los ${formatoCantidad(r.cambian)}, ${formatoCantidad(r.conEstrategia)} ` +
          `${r.conEstrategia === 1 ? 'tiene' : 'tienen'} estrategia (se recalculan con su método)` +
          (r.sinEstrategia > 0
            ? ` y ${formatoCantidad(r.sinEstrategia)} no (no cumplen una regla: se les propone el precio más cercano que la cumple).`
            : '.')
      );
    }
  }
  partes.push(`${cantidadDe(r.quedanIgual, 'queda igual', 'quedan igual')}.`);
  if (r.noSeCalculan > 0) {
    const uno = r.noSeCalculan === 1;
    partes.push(
      `${cantidadDe(r.noSeCalculan, 'tiene', 'tienen')} estrategia pero no se ${uno ? 'puede' : 'pueden'} calcular ` +
        `(${uno ? 'le' : 'les'} falta competencia, costo o precio de lista): no se ${uno ? 'toca' : 'tocan'}.`
    );
  }
  // Ronda final C1: una pausada sigue siendo la dueña de sus productos.
  if (r.deUnaPausada > 0) {
    partes.push(
      r.deUnaPausada === 1
        ? '1 es de una estrategia apagada: no se toca.'
        : `${formatoCantidad(r.deUnaPausada)} son de estrategias apagadas: no se tocan.`
    );
  }
  if (r.sinCosto > 0) {
    partes.push(
      r.sinCosto === 1
        ? 'El que no tiene costo no lo miden las reglas de margen.'
        : `Los ${formatoCantidad(r.sinCosto)} sin costo no los miden las reglas de margen.`
    );
  }
  return partes.join(' ');
}

/** Las que no vuelven por descarte y las que esperan una vuelta (D12): no se tocan. */
export function losQueNoVuelvenEnPalabras(r: ResumenDelRecalculo): string {
  const partes: string[] = [];
  if (r.noVuelven > 0) {
    partes.push(
      r.noVuelven === 1
        ? '1 no vuelve porque la descartaste y el precio sería casi el mismo.'
        : `${formatoCantidad(r.noVuelven)} no vuelven porque las descartaste y el precio sería casi el mismo.`
    );
  }
  if (r.esperanUnaVuelta > 0) {
    partes.push(
      r.esperanUnaVuelta === 1
        ? '1 espera la vuelta de una estrategia vencida: no se toca.'
        : `${formatoCantidad(r.esperanUnaVuelta)} esperan la vuelta de una estrategia vencida: no se tocan.`
    );
  }
  return partes.join(' ');
}

/** «Nada se aplica solo»: cuántos van, cuántas pendientes se reemplazan y cuántas no vuelven (D12). */
export function nadaSeAplicaSolo(r: ResumenDelRecalculo, total: number): string {
  const partes = [
    total === 1
      ? 'Va a Por decidir, aunque sea de una estrategia que aplica sola.'
      : `Los ${formatoCantidad(total)} van a Por decidir, también los de estrategias que aplican solas.`,
  ];
  if (r.seReemplazan > 0) {
    partes.push(
      `De ${cantidadDe(r.yaEsperan, 'que ya espera', 'que ya esperan')}, ` +
        `${r.seReemplazan === 1 ? '1 se reemplaza' : `${formatoCantidad(r.seReemplazan)} se reemplazan`} por el cálculo nuevo.`
    );
  }
  if (r.seCorrigen > 0) {
    partes.push(
      r.seCorrigen === 1
        ? '1 que esperaba queda en el precio de hoy: una regla de hoy no deja moverlo.'
        : `${formatoCantidad(r.seCorrigen)} que esperaban quedan en el precio de hoy: las reglas de hoy no dejan moverlos.`
    );
  }
  const losQueNoVuelven = losQueNoVuelvenEnPalabras(r);
  if (losQueNoVuelven !== '') partes.push(losQueNoVuelven);
  return partes.join(' ');
}

/** «Calculado hace un momento con las 3 reglas encendidas, sobre los 4.407 productos activos.» */
export function encabezadoDeLaVistaPrevia(
  v: {
    calculadoEn: string;
    resumen: Pick<ResumenDelRecalculo, 'reglas' | 'activos'>;
    /** Spec 294 (D-R4-4): con cambios sin aplicar, se calculó con las reglas como quedan. */
    cambiosSinAplicar?: unknown[];
  },
  ahora: number = Date.now()
): string {
  const { reglas, activos } = v.resumen;
  const cambios = v.cambiosSinAplicar?.length ?? 0;
  const conReglas =
    cambios > 0
      ? `con las reglas como quedan al aplicar ${cambiosEnTexto(cambios)}`
      : reglas === 0
        ? 'sin reglas encendidas'
        : reglas === 1
          ? 'con la regla encendida'
          : `con las ${formatoCantidad(reglas)} reglas encendidas`;
  const sobre =
    activos === 1 ? 'el producto activo' : `los ${formatoCantidad(activos)} productos activos`;
  return `Calculado ${haceCuanto(v.calculadoEn, ahora)} ${conReglas}, sobre ${sobre}.`;
}

/** Spec 294 (D-R4-4): «1 cambio» / «3 cambios». */
export function cambiosEnTexto(n: number): string {
  return n === 1 ? '1 cambio' : `${formatoCantidad(n)} cambios`;
}

/**
 * Spec 294 (D-R4-4): qué hace un cambio sin aplicar, en la lista «Lo que se va a
 * aplicar» de la ventana: «nueva», «se apaga», «se enciende», «se borra», «cambia …».
 */
export function queHaceElCambio(c: Pick<CambioSinAplicar, 'tipo' | 'campos' | 'enciende'>): string {
  if (c.tipo === 'NUEVA') return 'nueva';
  if (c.tipo === 'BORRAR') return 'se borra';
  if (c.enciende === false) return 'se apaga';
  if (c.enciende === true) return 'se enciende';
  return `cambia ${camposEnPalabras(c.campos)}`;
}

/** D11: «Cambiaste «No ganar menos de 50 %» hace 2 h». */
export function avisoDeReglasCambiadas(r: ReglasSinRecalcular, ahora: number = Date.now()): string {
  const cuando = haceCuanto(r.en, ahora);
  return r.cuantas === 1
    ? `Cambiaste «${r.ultima}» ${cuando}`
    : `Cambiaste ${formatoCantidad(r.cuantas)} reglas desde el último recálculo; la última, «${r.ultima}», ${cuando}`;
}

type CambiosSinAplicar = NonNullable<EstadoDelRecalculo['cambiosSinAplicar']>;

/** Spec 294 (D-R4-4): «3 cambios sin aplicar: todavía no rigen.» (1: «… no rige.»). */
export function avisoDeCambiosSinAplicar(c: CambiosSinAplicar): string {
  return c.cuantos === 1
    ? '1 cambio sin aplicar: todavía no rige.'
    : `${formatoCantidad(c.cuantos)} cambios sin aplicar: todavía no rigen.`;
}

/** Spec 294: «El último, en «Margen 30 %», lo hizo Ana López hace 2 h.» (sin quién, sin «lo hizo»). */
export function detalleDeCambiosSinAplicar(
  c: CambiosSinAplicar,
  ahora: number = Date.now()
): string {
  const cuando = haceCuanto(c.ultimo.en, ahora);
  return c.ultimo.por
    ? `El último, en «${c.ultimo.regla}», lo hizo ${c.ultimo.por} ${cuando}.`
    : `El último, en «${c.ultimo.regla}», ${cuando}.`;
}

/** Spec 275 (R1-d): cuántas de las que esperan se calcularon sin el cambio. Sin el dato, la frase de antes. */
export function esperanEnPalabras(r: ReglasSinRecalcular): string {
  const cambio = r.cuantas === 1 ? 'ese cambio' : 'esos cambios';
  const n = r.esperanSinElCambio;
  if (n == null) return `Las propuestas que esperan todavía se calcularon sin ${cambio}.`;
  if (n === 0) return `No hay propuestas esperando que se hayan calculado antes de ${cambio}.`;
  return n === 1
    ? `1 propuesta que espera en Por decidir se calculó sin ${cambio}.`
    : `${formatoCantidad(n)} propuestas que esperan en Por decidir se calcularon sin ${cambio}.`;
}

/**
 * Spec 275 (R1-f): qué mueve el recálculo. Medido en el motor: el rango de
 * «No mover más de… por vez» se arma alrededor del precio de hoy
 * (`rangos.service.ts`, MAX_CHANGE_PERCENT), así que el precio de hoy siempre
 * lo cumple y nunca agrega un producto sin estrategia.
 */
export const COMO_RECALCULA =
  'Con estrategia: se recalcula con su método y las reglas lo limitan. Sin estrategia: sólo cambia si el precio de hoy no cumple una regla de piso, techo, margen o distancia al más barato, o si dos reglas están en conflicto. «No mover más de… por vez» nunca agrega productos: sólo frena lo que una estrategia propone.';
